import { Box, BoxProps, HStack, Text } from "@chakra-ui/react"
import { useToolbarPosition } from "@pathwright/ux/src/components/augmentation/AugmentationPositionedToolbar"
import {
  AugmentationToolbarButton,
  AugmentationToolbarOption,
  AugmentationToolbarOptions,
  useAugmentationToolbar
} from "@pathwright/ux/src/components/augmentation/AugmentationToolbar"
import { MotionProps, motion } from "framer-motion"
import useChangelogAugmentationToolbarOption from "../../changelog/augmentation/useChangelogAugmentationToolbarOption"
import useIntercomAugmentationToolbar from "../../intercom/augmentation/useIntercomAugmentationToolbar"
import useGroupAugmentationToolbarOption from "../../mentor-group/augmentation/useGroupAugmentationToolbarOption"
import useNotificationsAugmentationToolbarOption from "../../notifications/augmentation/useNotificationsAugmentationToolbarOption"
import { usePathCreatorContext } from "../../path/create/PathCreatorContext"
import Pathicon from "../../pathicon/Pathicon"
import { usePathwrightContext } from "../../pathwright/PathwrightContext"
import useUserAugmentationToolbarOption from "../../user/augmentation/useUserAugmentationToolbarOption"
import { canEditLibrary } from "../../user/permissions"
import { useHomeRegistrationVars } from "../HomeRegistrationQueryVars"
import useRemovedRegistration from "../useRemovedRegistration"
import HomeLibraryAugmentation from "./HomeLibraryAugmentation"
import HomeSuperuserAugmentation from "./superuser/HomeSuperuserAugmentation"

const LibraryRenderToolbarComponent: AugmentationToolbarOption["ToolbarComponent"] =
  (props) => {
    const registration = useRemovedRegistration(useHomeRegistrationVars())
    // Only running the animation when a registration has been removed.
    const iconMotionProps: MotionProps & { key?: number } = registration
      ? {
          // The key prop is a simple trick for manually triggering the
          // animation to execute.
          key: registration.id,
          animate: {
            scale: [1.2, 1]
          },
          initial: { scale: 0.8 },
          transition: { type: "spring" }
        }
      : {}

    // The set aside icon + "+1" animates outward then disappears.
    const setAsideMotionProps: BoxProps & MotionProps = registration
      ? {
          // The key prop is a simple trick for manually triggering the
          // animation to execute.
          key: registration.id,
          animate: {
            opacity: [1, 1, 0],
            transform: "translate(-100%, -50%)",
            display: "block"
          },
          initial: {
            opacity: 0,
            transform: "translate(0, -50%)",
            display: "none"
          },
          transition: { type: "spring" }
        }
      : {}

    return (
      <motion.div {...iconMotionProps}>
        <AugmentationToolbarButton {...props} />
        <Box
          as={motion.div}
          pos="absolute"
          top="50%"
          transform="translate(0, -50%)"
          display="none"
          {...setAsideMotionProps}
        >
          <HStack>
            <Pathicon
              icon="sidebar-bottom"
              transform="rotate(-90deg)"
              color="white"
            />
            <Text as="span" color="white">
              +1
            </Text>
          </HStack>
        </Box>
      </motion.div>
    )
  }

function useLibraryAugmentationToolbarOption(): AugmentationToolbarOption | null {
  return {
    key: "library",
    icon: "book-fill",
    label: "Your Library",
    ToolbarComponent: LibraryRenderToolbarComponent,
    AugmentationComponent: HomeLibraryAugmentation
  }
}

function useSuperuserAugmentationToolbarOption(): AugmentationToolbarOption | null {
  return {
    key: "superuser-settings",
    icon: "sparkles",
    label: "Super Admin",
    AugmentationComponent: HomeSuperuserAugmentation
  }
}

function useAddPathToolbarOption(): AugmentationToolbarOption | null {
  const pwContext = usePathwrightContext()

  // Conditionally insert the AddPathButton into the toolbar
  // when the toolbar is at the bottom of the screen
  // (and when user has permissions).
  const showAddPathOption = useToolbarPosition(
    {
      bottom: canEditLibrary(pwContext),
      side: false
    },
    true /* allowOverlay */
  )

  const { setInitialValue } = usePathCreatorContext()
  function handleOnAddPath() {
    setInitialValue({ resource: null, cohort: null })
  }

  return showAddPathOption
    ? {
        key: "add_path",
        icon: "plus",
        label: "Add Path",
        color: "brand.500",
        variant: "brandReversed",
        onClick: handleOnAddPath
      }
    : null
}

export function useHomeAugmentationToolbarOptions() {
  const options: AugmentationToolbarOptions = [
    [
      useNotificationsAugmentationToolbarOption(),
      useLibraryAugmentationToolbarOption(),
      useUserAugmentationToolbarOption(),
      useGroupAugmentationToolbarOption()
    ],
    [
      useIntercomAugmentationToolbar(),
      useChangelogAugmentationToolbarOption(),
      useSuperuserAugmentationToolbarOption()
    ],
    [useAddPathToolbarOption()]
  ]

  return options
}

function useHomeAugmentationToolbar() {
  return useAugmentationToolbar(useHomeAugmentationToolbarOptions())
}

export default useHomeAugmentationToolbar
